/**
 * Content field helpers for AdminDashboard
 * Handles multilingual field operations
 */

/**
 * Get multilingual field value
 */
export const getFieldValue = (obj, field, lang) => {
  if (!obj || !obj[field]) return '';
  const val = obj[field];
  if (typeof val === 'object' && ('en' in val || 'ko' in val || 'ja' in val)) {
    return val[lang] || '';
  }
  return val;
};

/**
 * Create setFieldValue function with content setter
 */
export const createFieldSetter = (setContent) => (section, field, value, lang) => {
  setContent(prev => {
    const currentVal = prev[section]?.[field];
    const isMultilingual = typeof currentVal === 'object' && 
      ('en' in currentVal || 'ko' in currentVal || 'ja' in currentVal);
    
    if (isMultilingual) {
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: { ...currentVal, [lang]: value }
        }
      };
    } else {
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: { 
            en: lang === 'en' ? value : (currentVal || ''), 
            ko: lang === 'ko' ? value : '', 
            ja: lang === 'ja' ? value : '' 
          }
        }
      };
    }
  });
};

/**
 * Get nested field value
 */
export const getNestedFieldValue = (content, section, subsection, field, lang) => {
  const val = content?.[section]?.[subsection]?.[field];
  if (!val) return '';
  if (typeof val === 'object' && ('en' in val || 'ko' in val || 'ja' in val)) {
    return val[lang] || '';
  }
  return val;
};

/**
 * Create setNestedFieldValue function with content setter
 */
export const createNestedFieldSetter = (setContent) => (section, subsection, field, value, lang) => {
  setContent(prev => {
    const currentVal = prev[section]?.[subsection]?.[field];
    const isMultilingual = typeof currentVal === 'object' && 
      ('en' in currentVal || 'ko' in currentVal || 'ja' in currentVal);
    
    if (isMultilingual) {
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section]?.[subsection],
            [field]: { ...currentVal, [lang]: value }
          }
        }
      };
    } else {
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [subsection]: {
            ...prev[section]?.[subsection],
            [field]: { 
              en: lang === 'en' ? value : (currentVal || ''), 
              ko: lang === 'ko' ? value : '', 
              ja: lang === 'ja' ? value : '' 
            }
          }
        }
      };
    }
  });
};

/**
 * Get array item value
 */
export const getArrayItemValue = (content, section, field, index, key, lang) => {
  const item = content?.[section]?.[field]?.[index];
  if (!item) return '';
  const val = item[key];
  if (typeof val === 'object' && ('en' in val || 'ko' in val || 'ja' in val)) {
    return val[lang] || '';
  }
  return val || '';
};

/**
 * Create setArrayItemValue function with content setter
 */
export const createArrayItemSetter = (setContent) => (section, field, index, key, value, lang) => {
  setContent(prev => {
    const items = [...(prev[section]?.[field] || [])];
    const currentVal = items[index]?.[key];
    const isMultilingual = typeof currentVal === 'object' && 
      ('en' in currentVal || 'ko' in currentVal || 'ja' in currentVal);
    
    if (isMultilingual) {
      items[index] = { ...items[index], [key]: { ...currentVal, [lang]: value } };
    } else {
      items[index] = { 
        ...items[index], 
        [key]: { 
          en: lang === 'en' ? value : (currentVal || ''), 
          ko: lang === 'ko' ? value : '', 
          ja: lang === 'ja' ? value : '' 
        } 
      };
    }
    
    return { ...prev, [section]: { ...prev[section], [field]: items } };
  });
};

/**
 * Create addArrayItem function with content setter
 */
export const createArrayItemAdder = (setContent) => (section, field, newItem) => {
  setContent(prev => ({
    ...prev,
    [section]: {
      ...prev[section],
      [field]: [...(prev[section]?.[field] || []), newItem]
    }
  }));
};

/**
 * Create removeArrayItem function with content setter
 */
export const createArrayItemRemover = (setContent) => (section, field, index) => {
  setContent(prev => ({
    ...prev,
    [section]: {
      ...prev[section],
      [field]: prev[section]?.[field]?.filter((_, i) => i !== index)
    }
  }));
};

/**
 * Create simple field updater
 */
export const createSimpleFieldUpdater = (setContent) => (section, field, value) => {
  setContent(prev => ({ 
    ...prev, 
    [section]: { ...prev[section], [field]: value } 
  }));
};

/**
 * Create analytics updater
 */
export const createAnalyticsUpdater = (setContent) => (field, value) => {
  setContent(prev => ({ 
    ...prev, 
    analytics: { ...prev.analytics, [field]: value } 
  }));
};

/**
 * Create design updater
 */
export const createDesignUpdater = (setContent) => (category, field, value) => {
  setContent(prev => ({
    ...prev,
    design: { 
      ...prev.design, 
      [category]: { ...prev.design?.[category], [field]: value } 
    }
  }));
};

/**
 * Create lead routing updater
 */
export const createLeadRoutingUpdater = (setContent) => (field, value) => {
  setContent(prev => ({
    ...prev,
    leadRouting: { 
      ...prev.leadRouting, 
      routes: { ...prev.leadRouting?.routes, [field]: value } 
    }
  }));
};

/**
 * Create auto-reply updaters
 *
 * `updateAutoReply(field, value)` sets a top-level field on `autoReply`.
 * `updateAutoReplyPath(pathArray, value)` sets a deeply-nested field like
 *   ['senderEmails', 'ja'] → autoReply.senderEmails.ja = value
 *   ['templates', 'en', 'subject'] → autoReply.templates.en.subject = value
 */
export const createAutoReplyUpdater = (setContent) => (field, value) => {
  setContent(prev => ({
    ...prev,
    autoReply: { ...prev.autoReply, [field]: value }
  }));
};

export const createAutoReplyPathUpdater = (setContent) => (path, value) => {
  if (!Array.isArray(path) || path.length === 0) return;
  setContent(prev => {
    const next = { ...prev, autoReply: { ...(prev.autoReply || {}) } };
    let cursor = next.autoReply;
    for (let i = 0; i < path.length - 1; i += 1) {
      const key = path[i];
      cursor[key] = { ...(cursor[key] || {}) };
      cursor = cursor[key];
    }
    cursor[path[path.length - 1]] = value;
    return next;
  });
};
